import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { TableCell, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '../ui/dropdown-menu';
import { Download, Edit, Trash2, History, StickyNote, Paperclip, MoreVertical, RefreshCw, Check } from 'lucide-react';
import { Invoice, InvoiceStatus, User } from '../../types';
import { SupportingDocumentsModal } from './SupportingDocumentsModal';
import { getStatusKey } from '../../utils/formatters';

interface InvoiceTableRowProps {
  invoice: Invoice;
  userRole?: string;
  showSelectionColumn?: boolean;
  /** Renders the contract rate cell; the parent decides which roles may see rates. */
  showRateColumn?: boolean;
  users?: User[];
  isSelected: boolean;
  onSelect: (checked: boolean) => void;
  onUpdate: (invoice: Invoice) => void;
  onDelete: (id: string) => void;
  onViewHistory: (invoice: Invoice) => void;
  onStatusChange?: (invoiceId: string, newStatus: InvoiceStatus) => void;
}

const getStatusBadgeStyle = (status: string): React.CSSProperties => {
  // Normalize status to uppercase and remove underscores for comparison
  const normalizedStatus = status.toUpperCase().replace(/_/g, '');
  
  switch (normalizedStatus) {
    case 'PENDING':
      // Warning - yellow/amber
      return { backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' };
    case 'UNDERREVIEW':
      // Default - gray
      return { backgroundColor: '#f3f4f6', color: '#1f2937', borderColor: '#e5e7eb' };
    case 'APPROVED':
      // Success - green
      return { backgroundColor: '#d1fae5', color: '#065f46', borderColor: '#a7f3d0' };
    case 'REJECTED':
      // Destructive - red
      return { backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' };
    case 'PAID':
      // Black with white text
      return { backgroundColor: '#000000', color: '#ffffff', borderColor: '#000000' };
    default:
      // Default gray - also log to console to see what we're getting
      console.log('Unknown status:', status, 'normalized:', normalizedStatus);
      return { backgroundColor: '#f3f4f6', color: '#1f2937', borderColor: '#e5e7eb' };
  }
};

export const InvoiceTableRow: React.FC<InvoiceTableRowProps> = ({
  invoice,
  userRole,
  showSelectionColumn = false,
  showRateColumn = false,
  users = [],
  isSelected,
  onSelect,
  onUpdate,
  onDelete,
  onViewHistory,
  onStatusChange,
}) => {
  const { t } = useTranslation('invoices');
  const { t: tCommon } = useTranslation('common');
  const [showSupportingDocs, setShowSupportingDocs] = useState(false);
  
  // Find the user for this invoice to get their contract rate
  const invoiceUser = users.find(u => u.id === invoice.userId);
  
  return (
    <>
      <TableRow data-testid={`invoice-row-${invoice.id}`}>
        {showSelectionColumn && (
          userRole === 'admin' || userRole === 'manager' ? (
            <TableCell>
              <Checkbox checked={isSelected} onCheckedChange={onSelect} data-testid={`invoice-checkbox-${invoice.id}`} />
            </TableCell>
          ) : (
            <TableCell />
          )
        )}
      
      <TableCell className="font-medium whitespace-nowrap">{invoice.invoiceMonth}</TableCell>
      
            <TableCell className="max-w-[140px] overflow-hidden">
              <Link 
                to={`/people/${invoice.userId}`}
                className="truncate block w-full text-primary hover:underline"
                title={invoice.userName}
              >
                {invoice.userName}
              </Link>
            </TableCell>
      
      <TableCell className="max-w-[140px] overflow-hidden">
        {invoice.extractedVendorName ? (
          <span className="truncate block w-full" title={invoice.extractedVendorName}>{invoice.extractedVendorName}</span>
        ) : (
          <span className="text-sm text-muted-foreground italic">{t('table.notExtracted')}</span>
        )}
      </TableCell>
      
      <TableCell>
        {invoice.overrideTotal != null ? (
          <div>
            <span className="font-semibold text-red-700">
              {invoice.extractedCurrency === 'USD' ? '$' : invoice.extractedCurrency || '$'}
              {invoice.overrideTotal.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
            <span className="text-xs text-muted-foreground ml-1">{t('table.override')}</span>
          </div>
        ) : invoice.extractedInvoiceTotal != null ? (
          <span className="font-semibold">
            {invoice.extractedCurrency === 'USD' ? '$' : invoice.extractedCurrency || '$'}
            {invoice.extractedInvoiceTotal.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground italic">{t('table.notExtracted')}</span>
        )}
      </TableCell>
      
      {showRateColumn && (
        <TableCell>
          {invoiceUser?.contractRate ? (
            <span className="text-sm font-medium">
              ${invoiceUser.contractRate.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground italic">-</span>
          )}
        </TableCell>
      )}
      
      <TableCell className="max-w-[200px] overflow-hidden">
        <div className="flex items-center gap-1.5">
          <a
            href={invoice.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline truncate"
            title={invoice.fileName}
          >
            {invoice.fileName}
          </a>
          {invoice.supportingDocumentsCount != null && invoice.supportingDocumentsCount > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground shrink-0">
                    <Paperclip className="h-3.5 w-3.5" />
                    {invoice.supportingDocumentsCount}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{invoice.supportingDocumentsCount} supporting document{invoice.supportingDocumentsCount > 1 ? 's' : ''}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </TableCell>
      
      <TableCell>
        <div className="flex items-center gap-2">
          <span 
            style={{
              ...getStatusBadgeStyle(invoice.status),
              display: 'inline-flex',
              alignItems: 'center',
              borderRadius: '0.375rem',
              paddingLeft: '0.625rem',
              paddingRight: '0.625rem',
              paddingTop: '0.125rem',
              paddingBottom: '0.125rem',
              fontSize: '0.75rem',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              border: '1px solid',
            }}
          >
            {tCommon(`status.${getStatusKey(invoice.status)}`)}
          </span>
          {(userRole === 'admin' || userRole === 'manager') && invoice.reviewerComments && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <StickyNote className="h-4 w-4 text-amber-500 cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">{invoice.reviewerComments}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </TableCell>
      
      <TableCell className="whitespace-nowrap">{new Date(invoice.createdAt).toLocaleDateString()}</TableCell>
      
      <TableCell>
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={`invoice-actions-trigger-${invoice.id}`}>
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid={`invoice-actions-menu-${invoice.id}`}>
              <DropdownMenuItem onClick={() => setShowSupportingDocs(true)} data-testid={`invoice-action-supporting-docs-${invoice.id}`}>
                <Paperclip className="mr-2 h-4 w-4" />
                Supporting Documents
              </DropdownMenuItem>
              
              <DropdownMenuItem asChild data-testid={`invoice-action-download-${invoice.id}`}>
                <a href={invoice.fileUrl} download className="flex items-center">
                  <Download className="mr-2 h-4 w-4" />
                  {tCommon('buttons.download')}
                </a>
              </DropdownMenuItem>
              
                            {(userRole === 'admin' || userRole === 'manager') && (
                              <>
                                <DropdownMenuSeparator />
                  
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger data-testid={`invoice-action-change-status-${invoice.id}`}>
                                    <RefreshCw className="mr-2 h-4 w-4" />
                                    {t('actions.changeStatus')}
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    {Object.values(InvoiceStatus)
                                      .filter((s) => userRole === 'admin' || s !== InvoiceStatus.PAID)
                                      .map((statusOption) => (
                                      <DropdownMenuItem
                                        key={statusOption}
                                        onClick={() => onStatusChange?.(invoice.id, statusOption)}
                                        disabled={invoice.status === statusOption}
                                        data-testid={`invoice-action-status-${statusOption.toLowerCase()}-${invoice.id}`}
                                      >
                                        <span 
                                          style={{
                                            ...getStatusBadgeStyle(statusOption),
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            borderRadius: '0.375rem',
                                            paddingLeft: '0.5rem',
                                            paddingRight: '0.5rem',
                                            paddingTop: '0.125rem',
                                            paddingBottom: '0.125rem',
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            whiteSpace: 'nowrap',
                                            border: '1px solid',
                                          }}
                                        >
                                          {tCommon(`status.${getStatusKey(statusOption)}`)}
                                        </span>
                                        {invoice.status === statusOption && <Check className="ml-auto h-4 w-4" />}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                  
                                <DropdownMenuItem onClick={() => onUpdate(invoice)} data-testid={`invoice-action-edit-${invoice.id}`}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  {t('actions.edit')}
                                </DropdownMenuItem>
                  
                                <DropdownMenuItem onClick={() => onViewHistory(invoice)} data-testid={`invoice-action-history-${invoice.id}`}>
                                  <History className="mr-2 h-4 w-4" />
                                  {t('actions.viewHistory')}
                                </DropdownMenuItem>
                              </>
                            )}
              
              {((userRole === 'admin' || userRole === 'manager') || invoice.status === InvoiceStatus.PENDING) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => onDelete(invoice.id)}
                    className="text-destructive focus:text-destructive"
                    data-testid={`invoice-action-delete-${invoice.id}`}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t('actions.delete')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
      </TableRow>
      
      {showSupportingDocs && (
        <SupportingDocumentsModal
          invoice={invoice}
          userRole={userRole}
          open={showSupportingDocs}
          onOpenChange={setShowSupportingDocs}
        />
      )}
    </>
  );
};
